import {mergeWith, isEmpty, uniqWith, isEqual} from 'lodash'

import {TOKEN_LOCALSTORAGE_KEY} from '../config'

const generateUserMenu = project => {
  const {id, role} = project
  const baseUrl = `/project/${id}`

  const projectSettingRoute = {
    id: 'settings',
    title: 'Settings',
    type: 'item',
    icon: 'settings',
    url: `${baseUrl}/settings`,
  }
  const storeSettingRoute = {
    id: 'storeSettings',
    title: 'Store Settings',
    type: 'item',
    icon: 'business',
    url: `${baseUrl}/storeSettings`,
  }
  const productRoute = {
    id: 'products',
    title: 'Products',
    type: 'item',
    icon: 'shopping_cart',
    url: `${baseUrl}/products`,
  }
  const orderRoute = {
    id: 'orders',
    title: 'Orders',
    type: 'item',
    icon: 'payment',
    url: `${baseUrl}/orders`,
  }
  const storageRoute = {
    id: 'storage',
    title: 'Inventory',
    type: 'item',
    icon: 'store',
    url: `${baseUrl}/inventory`,
  }

  const aiSettingRoute = {
    id: 'aiSetting',
    title: 'AI Settings',
    type: 'item',
    icon: 'chat_bubble_outline',
    url: `${baseUrl}/aiSettings`,
  }

  const chatRoute = {
    id: 'chat',
    title: 'Chat',
    type: 'item',
    icon: 'chat_bubble_outline',
    url: `${baseUrl}/chat`,
  }
  const ownerRoute = [
    projectSettingRoute,
    storeSettingRoute,
    productRoute,
    orderRoute,
    chatRoute,
    storageRoute,
    aiSettingRoute,
  ]

  const adminRoute = [
    storeSettingRoute,
    productRoute,
    orderRoute,
    chatRoute,
    storageRoute,
    aiSettingRoute,
  ]

  let route
  switch (role) {
    case 'admin':
      route = adminRoute
      break
    case 'owner':
      route = ownerRoute
      break
    default:
      route = []
      break
  }

  const fuseNavigationConfig = [
    {
      id: 'applications',
      title: 'Applications',
      type: 'group',
      icon: 'apps',
      children: route,
    },
  ]

  return fuseNavigationConfig
}

const permutation = object => {
  const keys = Object.keys(object)
  if (keys.length === 0) {
    return object
  }
  const r = []
  const c = (part, index) => {
    object[keys[index]].forEach(a => {
      const p = part.concat(a)
      if (p.length === keys.length) {
        r.push(p)
        return
      }
      c(p, index + 1)
    })
  }

  c([], 0)
  return r
}
const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'THB',
  minimumFractionDigits: 2,
})

const findNextAvaliableName = (
  obj,
  func,
  startName = 'placeholder',
  index = 0,
  withNumber = false,
) => {
  const placeholderKey = startName
  let count = 0
  let tryKey = withNumber ? `${placeholderKey}-${1}` : placeholderKey
  if (!obj) {
    return tryKey
  }
  for (let i = 2; func(obj, tryKey) || count++ < index; i++) {
    tryKey = `${placeholderKey}-${i}`
  }
  return tryKey
}

const keyDelete = (data, removeKeys) => {
  if (
    data instanceof File ||
    typeof data === 'string' ||
    typeof data === 'number' ||
    typeof data === 'boolean' ||
    typeof data === 'undefined'
  ) {
    return data
  }
  if (data === null) {
    return null
  }
  if (Array.isArray(data)) {
    return data.map(i => keyDelete(i, removeKeys))
  }
  return Object.keys(data)
    .filter(i => removeKeys.indexOf(i) === -1)
    .reduce(
      (obj, key) => ({...obj, [key]: keyDelete(data[key], removeKeys)}),
      {},
    )
}

const mapStoreSettingsToBackEnd = data => {
  if (data.data === false) {
    return {}
  }
  // checkbox
  if (data.data === true) {
    return Object.keys(data.child).reduce(
      (acc, key) => ({
        ...acc,
        [key]: mapStoreSettingsToBackEnd(data.child[key]),
      }),
      {},
    )
  }
  if (data.data) {
    if (Array.isArray(data.data)) {
      return data.data.map(d => {
        return Object.keys(d).reduce(
          (acc, key) => ({
            ...acc,
            [key]: mapStoreSettingsToBackEnd(d[key]),
          }),
          {},
        )
      })
    }
    return data.data
  }
  if (data.data === '') {
    return ''
  }
  if (!data.child) {
    return {}
  }
  return Object.keys(data.child).reduce(
    (acc, key) => ({
      ...acc,
      [key]: mapStoreSettingsToBackEnd(data.child[key]),
    }),
    {},
  )
}

const mapStoreSettingsToFrontEnd = data => {
  if (!data) {
    return {}
  }
  const mapArrayStoreSetting = elementInArray => {
    if (
      typeof elementInArray === 'string' ||
      typeof elementInArray === 'number' ||
      typeof elementInArray === 'boolean' ||
      typeof elementInArray === 'undefined'
    ) {
      return {data: elementInArray}
    }
    return Object.keys(elementInArray).reduce(
      (acc, key) => ({
        ...acc,
        [key]: mapStoreSettingsToFrontEnd(elementInArray[key]),
      }),
      {},
    )
  }

  if (Array.isArray(data)) {
    return {data: data.map(arrayElement => mapArrayStoreSetting(arrayElement))}
  }
  if (
    typeof data === 'string' ||
    typeof data === 'number' ||
    typeof data === 'boolean' ||
    typeof data === 'undefined'
  ) {
    return {data}
  }
  return {
    child: Object.keys(data).reduce(
      (acc, key) => ({
        ...acc,
        [key]: mapStoreSettingsToFrontEnd(data[key]),
      }),
      {},
    ),
  }
}

const mergeWithDefaultStoreSettings = (defaultData, realData) => {
  return mergeWith(defaultData, realData, (defaultValue, dataValue) => {
    if (
      defaultValue &&
      defaultValue.type === 'checkbox' &&
      dataValue &&
      !isEmpty(dataValue.child)
    ) {
      return {...mergeWith(defaultValue, dataValue), data: true}
    }
    return undefined
  })
}

const addDays = (date, days) => {
  const result = new Date(date)
  result.setDate(result.getDate() + days)
  return result
}

const convertToReadableDate = date => {
  if (date) {
    const d = new Date(date)
    const day = d.getDate()
    const month = d.getMonth() + 1
    const year = d.getFullYear()
    return `${day}/${month}/${year}`
  }
  return '-'
}

const convertToReadableTime = date => {
  if (date) {
    const d = new Date(date)
    return d.toLocaleTimeString('th').replace(/(.*)\D\d+/, '$1')
  }
  return '-'
}

const convertToReadableDateTime = date => {
  if (date) {
    const d = new Date(date)
    const day = d.getDate()
    const month = d.getMonth() + 1
    const year = d.getFullYear()
    const times = d.toLocaleTimeString('th').replace(/(.*)\D\d+/, '$1')
    return `${day}/${month}/${year} ${times}`
  }
  return '-'
}

const getLoginToken = () => {
  return localStorage.getItem(TOKEN_LOCALSTORAGE_KEY)
}

const setLoginToken = token => {
  localStorage.setItem(TOKEN_LOCALSTORAGE_KEY, token)
}

const logout = () => {
  localStorage.removeItem(TOKEN_LOCALSTORAGE_KEY)
  window.location.reload()
}

const getAllTypesFromSKU = productSKUs => {
  let allTypes = []
  productSKUs.forEach(productSKU => {
    productSKU.productType.forEach(type => {
      const keyIndex = allTypes.findIndex(({value: _a, ...newType}) => {
        const {key} = type
        return JSON.stringify(key) === JSON.stringify(newType)
      })
      if (keyIndex !== -1) {
        const {key, ...temp} = type
        allTypes[keyIndex].value = [...allTypes[keyIndex].value, temp]
      } else {
        const {key, ...temp} = type
        allTypes = [...allTypes, {...key, value: [temp]}]
      }
    })
  })
  return allTypes.map(type => ({
    ...type,
    value: uniqWith(type.value, isEqual),
  }))
}

export {
  generateUserMenu,
  permutation,
  currencyFormatter,
  findNextAvaliableName,
  keyDelete,
  mapStoreSettingsToBackEnd,
  mapStoreSettingsToFrontEnd,
  mergeWithDefaultStoreSettings,
  addDays,
  convertToReadableDate,
  convertToReadableTime,
  convertToReadableDateTime,
  getLoginToken,
  setLoginToken,
  logout,
  getAllTypesFromSKU,
}



// WEBPACK FOOTER //
// ./src/utils/index.js